import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  FaUser,
  FaQrcode,
} from "react-icons/fa6";
import {
  IoChevronForward,
  IoPowerOutline,
  IoNotificationsOutline,
  IoCalendarOutline,
  IoTicketOutline,
  IoBagHandleOutline,
  IoFastFoodOutline,
  IoGiftOutline,
} from "react-icons/io5";
import {
  FiHexagon,
  FiGift,
  FiSend,
  FiXCircle,
  FiFileText,
  FiMessageSquare,
  FiStar,
  FiBell,
  FiSettings,
} from "react-icons/fi";

import { SUBDOMAIN } from "../../assets/meta/meta";
import { useAuthContext } from "../../Components/AuthContext";
import MobileViewContainer from "../../Components/MobileViewContainer";
import NavBottomBar from "../../Components/Common/NavBottomBar";
import UserQrModal from "../../Components/modal/UserQrModal";
import { useUserStore } from "../../store/store";
import {
  NOTIFICATION_COUNT_QUERY_KEY,
  notificationService,
} from "../../api/services/notification.service";
import { MYPAGE_SUMMARY_QUERY_KEY, mypageService } from "../../api/services/mypage.service";
import { SITE_ID } from "../../assets/meta/meta";

// ─── Permission helpers ───────────────────────────────────────────────────────

function usePermissions(userInfo) {
  const capabilitySet = React.useMemo(
    () => new Set(Array.isArray(userInfo?.capabilities) ? userInfo.capabilities : []),
    [userInfo?.capabilities],
  );
  const hasCapability = (cap) =>
    Boolean(userInfo?.is_global_admin || capabilitySet.has(cap));
  const isCurrentSiteRole =
    userInfo?.site_permission?.site === SITE_ID &&
    Number.isInteger(userInfo?.site_permission?.permission);
  const isAdmin =
    hasCapability("crm_dashboard") ||
    (isCurrentSiteRole && [1, 2, 5, 6].includes(userInfo?.site_permission?.permission));
  const isStaff =
    hasCapability("site_staff") ||
    (isCurrentSiteRole &&
      [0, 1, 2, 3, 4, 5, 6].includes(userInfo?.site_permission?.permission));
  const permType = isAdmin ? "ADMIN" : isStaff ? "STAFF" : "CUSTOMER";
  return { isAdmin, isStaff, permType };
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function RoleBadge({ type }) {
  const styles = {
    ADMIN: "bg-gray-900 text-white",
    STAFF: "bg-green-500 text-white",
    CUSTOMER: "border border-gray-300 text-gray-500",
  };
  return (
    <span
      className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${styles[type] || styles.CUSTOMER}`}
    >
      {type}
    </span>
  );
}

function ProfileCard({ userInfo, permType, isAdmin, onQrClick, navigate, unreadCount }) {
  const unreadLabel = unreadCount > 99 ? "99+" : unreadCount;
  return (
    <div className="bg-white px-4 py-5">
      <div className="flex items-center justify-between">
        {/* Avatar + info */}
        <button
          type="button"
          className="flex min-w-0 flex-1 items-center gap-3 text-left"
          onClick={() => navigate("/my-page/settings")}
        >
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-400">
            <FaUser size={20} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-lg font-semibold" style={{ fontFamily: "Gmarket-M" }}>
                {userInfo?.username || "게스트"}
              </span>
              <RoleBadge type={permType} />
            </div>
            <p className="mt-0.5 truncate text-sm text-gray-500">
              {userInfo?.email ||
                userInfo?.phone_num?.replace(/(\d{3})(\d{4})(\d{4})/, "$1-$2-$3") ||
                ""}
            </p>
          </div>
          <IoChevronForward size={16} className="shrink-0 text-gray-400" />
        </button>

        {/* Action buttons */}
        <div className="ml-3 flex shrink-0 items-center gap-2">
          {/* QR */}
          <button
            type="button"
            onClick={onQrClick}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 active:opacity-70"
          >
            <FaQrcode size={17} />
          </button>
          {/* Notification */}
          <button
            type="button"
            onClick={() => navigate("/notification")}
            className="relative flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 active:opacity-70"
          >
            <IoNotificationsOutline size={18} />
            {unreadCount > 0 && (
              <span className="absolute -right-1 -top-1 flex min-w-4 items-center justify-center rounded-full bg-red-500 px-1 py-0 text-[10px] font-semibold leading-4 text-white">
                {unreadLabel}
              </span>
            )}
          </button>
          {/* Admin CRM link */}
          {isAdmin && (
            <button
              type="button"
              onClick={() =>
                window.open(
                  `https://${SUBDOMAIN}crm.${window.AMZ_ENV?.VITE_DOMAIN || "amazingticket.site"}/`,
                  "_blank",
                )
              }
              className="flex items-center gap-1 rounded-full bg-gray-900 px-3 py-1.5 text-xs font-semibold text-white"
              style={{ fontFamily: "Gmarket-M" }}
            >
              관리자
              <IoChevronForward size={12} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function CountBadge({ count }) {
  if (!count) return null;
  return (
    <span className="absolute -right-1 -top-1 flex min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold leading-4 text-white">
      {count > 99 ? "99+" : count}
    </span>
  );
}

function QuickMenu({ tabs, summary, navigate }) {
  return (
    <div className="mx-4 mt-3 rounded-xl border border-gray-100 bg-white shadow-sm">
      <div className="flex">
        {tabs.map((item, idx) => (
          <button
            key={item.id}
            type="button"
            onClick={() => navigate(item.path)}
            className={`relative flex flex-1 flex-col items-center gap-1.5 py-4 active:opacity-60 ${
              idx < tabs.length - 1 ? "border-r border-gray-100" : ""
            }`}
          >
            <div className="relative">
              {item.icon}
              <CountBadge count={summary?.[item.countKey]} />
            </div>
            <span className="text-[11px] text-gray-600">{item.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function ListSection({ title, children }) {
  return (
    <div className="mt-4">
      <div className="px-4 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">
        {title}
      </div>
      <div className="bg-white">{children}</div>
    </div>
  );
}

function ListItem({ icon, label, onClick, disabled = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex w-full items-center justify-between border-b border-gray-50 px-4 py-3.5 text-left active:bg-gray-50 disabled:opacity-40"
    >
      <div className="flex items-center gap-3 text-gray-700">
        <span className="text-gray-400">{icon}</span>
        <span className="text-sm">{label}</span>
      </div>
      <IoChevronForward size={15} className="text-gray-300" />
    </button>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function MyPage() {
  const { userInfo, logout } = useAuthContext();
  const navigate = useNavigate();
  const [site] = useUserStore((state) => [state.site]);
  const [showQrModal, setShowQrModal] = React.useState(false);

  const bottomBarMenuVisibility = site?.bottom_bar_menu_visibility || {};
  const myPageMoreMenuVisibility = site?.my_page_more_menu_visibility || {};

  const { isAdmin, permType } = usePermissions(userInfo);

  // Notification count
  const { data: notifData } = useQuery({
    queryKey: NOTIFICATION_COUNT_QUERY_KEY,
    queryFn: async () => {
      const res = await notificationService.getNotificationCount();
      return res.data;
    },
    enabled: !!userInfo,
    refetchInterval: 60000,
    staleTime: 30000,
  });
  const unreadCount = notifData?.count || 0;

  // Summary counts
  const { data: summary } = useQuery({
    queryKey: MYPAGE_SUMMARY_QUERY_KEY,
    queryFn: async () => {
      const res = await mypageService.getSummary();
      return res.data;
    },
    enabled: !!userInfo,
    staleTime: 30000,
  });

  // Quick icon menu tabs
  const quickTabs = React.useMemo(
    () =>
      [
        {
          id: "reservation",
          label: "예약",
          path: "/my-page/reservation",
          icon: <IoCalendarOutline size={22} className="text-gray-600" />,
          countKey: "reservation_count",
          visible: bottomBarMenuVisibility.reservation !== false,
        },
        {
          id: "ticket",
          label: "티켓",
          path: "/my-page/ticket",
          icon: <IoTicketOutline size={22} className="text-gray-600" />,
          countKey: "ticket_count",
          visible: bottomBarMenuVisibility.ticket !== false,
        },
        {
          id: "order",
          label: "쇼핑",
          path: "/my-page/shopping-purchase",
          icon: <IoBagHandleOutline size={22} className="text-gray-600" />,
          countKey: "order_count",
          visible: bottomBarMenuVisibility.shopping !== false,
        },
        {
          id: "table_order",
          label: "오더",
          path: "/my-page/table-order",
          icon: <IoFastFoodOutline size={22} className="text-gray-600" />,
          countKey: "table_order_count",
          visible: true,
        },
        {
          id: "coupon",
          label: "쿠폰",
          path: "/my-page/coupon",
          icon: <IoGiftOutline size={22} className="text-gray-600" />,
          countKey: "coupon_count",
          visible: true,
        },
      ].filter((t) => t.visible !== false),
    [bottomBarMenuVisibility],
  );

  // "내 활동" section items
  const activityItems = [
    myPageMoreMenuVisibility.amazingpass !== false && {
      label: "어메이징패스",
      icon: <FiHexagon size={17} />,
      path: "/my-page/amazingpass",
    },
    myPageMoreMenuVisibility.gift_box !== false && {
      label: "선물함",
      icon: <FiGift size={17} />,
      path: "/my-page/gift-box",
    },
    myPageMoreMenuVisibility.gift_send !== false && {
      label: "보낸선물",
      icon: <FiSend size={17} />,
      path: "/my-page/gift-send",
    },
    myPageMoreMenuVisibility.cancel_history !== false && {
      label: "취소내역",
      icon: <FiXCircle size={17} />,
      path: "/my-page/cancel",
    },
    {
      label: "스탬프",
      icon: <FiStar size={17} />,
      path: "/my-page/stamp",
    },
  ].filter(Boolean);

  // "서비스" section items
  const serviceItems = [
    {
      label: "공지사항",
      icon: <FiBell size={17} />,
      path: "/notice",
    },
    myPageMoreMenuVisibility.inquiry !== false && {
      label: "문의하기",
      icon: <FiMessageSquare size={17} />,
      path: "/inquiry",
    },
  ].filter(Boolean);

  // "계정" section items
  const accountItems = [
    {
      label: "사용자 설정",
      icon: <FiSettings size={17} />,
      path: "/my-page/settings",
    },
    {
      label: "로그아웃",
      icon: <IoPowerOutline size={17} />,
      action: async () => {
        await logout();
      },
    },
  ];

  // "관리자" section items (admin only)
  const adminItems = isAdmin
    ? [
        { label: "B2B 쿠폰", icon: <FiFileText size={17} />, path: "/my-page/b2b/coupon" },
        { label: "B2B 예약권", icon: <FiFileText size={17} />, path: "/my-page/b2b/precoupon" },
        { label: "B2B 무료쿠폰", icon: <FiFileText size={17} />, path: "/my-page/b2b/voucher" },
      ]
    : [];

  const handleItemClick = (item) => {
    if (item.action) {
      item.action();
      return;
    }
    if (item.path) navigate(item.path);
  };

  return (
    <React.Fragment>
      <MobileViewContainer>
        <div className="min-h-screen bg-gray-50 pb-24">
          {/* Header */}
          <div className="sticky top-0 z-10 flex items-center bg-white px-4 py-3 shadow-[0_1px_0_#eee]">
            <h1
              className="text-base font-semibold text-gray-900"
              style={{ fontFamily: "Gmarket-M" }}
            >
              마이페이지
            </h1>
          </div>

          {/* Profile card */}
          <ProfileCard
            userInfo={userInfo}
            permType={permType}
            isAdmin={isAdmin}
            onQrClick={() => setShowQrModal(true)}
            navigate={navigate}
            unreadCount={unreadCount}
          />

          {/* Quick icon menu */}
          <QuickMenu tabs={quickTabs} summary={summary} navigate={navigate} />

          {/* List sections */}
          {activityItems.length > 0 && (
            <ListSection title="내 활동">
              {activityItems.map((item) => (
                <ListItem
                  key={item.label}
                  icon={item.icon}
                  label={item.label}
                  onClick={() => handleItemClick(item)}
                />
              ))}
            </ListSection>
          )}

          <ListSection title="서비스">
            {serviceItems.map((item) => (
              <ListItem
                key={item.label}
                icon={item.icon}
                label={item.label}
                onClick={() => handleItemClick(item)}
              />
            ))}
          </ListSection>

          <ListSection title="계정">
            {accountItems.map((item) => (
              <ListItem
                key={item.label}
                icon={item.icon}
                label={item.label}
                onClick={() => handleItemClick(item)}
              />
            ))}
          </ListSection>

          {adminItems.length > 0 && (
            <ListSection title="관리자">
              {adminItems.map((item) => (
                <ListItem
                  key={item.label}
                  icon={item.icon}
                  label={item.label}
                  onClick={() => handleItemClick(item)}
                />
              ))}
            </ListSection>
          )}
        </div>
        <NavBottomBar activeBar={5} />
      </MobileViewContainer>

      <UserQrModal
        isOpen={showQrModal}
        toggle={() => setShowQrModal(!showQrModal)}
        userInfo={userInfo}
      />
    </React.Fragment>
  );
}
